import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import Navbar from "@/components/Navbar";
import RichText from "@/components/RichText";
import { STATES } from "@/lib/states";
import { Search, MapPin, ExternalLink, Loader2, ShieldCheck, BookOpen, FileText, Building2, Newspaper, Briefcase, AlertCircle } from "lucide-react";
import SiteFooter from "@/components/SiteFooter";

const CATEGORY_ICON = {
 tribunal: BookOpen,
 licence: ShieldCheck,
 company: Building2,
 media: Newspaper,
 council: FileText,
};

const CATEGORY_LABEL = {
 tribunal: "Tribunal & court decisions",
 licence: "Agent licence",
 company: "Company & directors",
 media: "Media & public records",
 council: "Council compliance",
};

export default function PropertyCheck() {
 const [address, setAddress] = useState("");
 const [state, setState] = useState("QLD");
 const [email, setEmail] = useState("");
 const [submitting, setSubmitting] = useState(false);
 const [result, setResult] = useState(null);
 const [error, setError] = useState("");

 const submit = async (e) => {
 e?.preventDefault();
 setError("");
 if (!address || address.trim().length < 6) {
 setError("Enter the full street address (number, street, suburb).");
 return;
 }
 setSubmitting(true);
 try {
 const { data } = await axios.post(`${API}/property-lookup`, {
 address: address.trim(),
 state,
 email: (email || "").trim(),
 });
 setResult(data);
 // Scroll to results
 setTimeout(() => {
 document.getElementById("check-results")?.scrollIntoView({ behavior: "smooth", block: "start" });
 }, 80);
 } catch (err) {
 setError(err.response?.data?.detail || "Something went wrong. Try again in a moment.");
 } finally {
 setSubmitting(false);
 }
 };

 // Group sources by category for the results display
 const grouped = result ? result.sources.reduce((acc, s) => {
 (acc[s.category] = acc[s.category] || []).push(s);
 return acc;
 }, {}) : {};

 return (
 <div className="min-h-screen bg-[#FAF7F0]">
 <Navbar />
 <main className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 py-12 sm:py-16">
 {/* Hero */}
 <div className="text-center mb-10">
 <div className="inline-flex items-center gap-2 px-3 py-1 bg-white border border-[#1B6C62]/30 mb-5">
 <Search size={14} strokeWidth={1.75} className="text-[#1B6C62]" />
 <span className="label-mono text-[#1B6C62]">Before you sign</span>
 </div>
 <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#0D2B52] mb-4 leading-tight">
 Check the rental.<br />Before you sign the lease.
 </h1>
 <p className="text-[#6B7280] max-w-2xl mx-auto leading-relaxed text-lg">
 Search every Australian public register for tribunal decisions, agent licences and disclosed issues against a property. All in one click.
 </p>
 </div>

 {/* Why this matters, short intro band above the form */}
 <div className="max-w-3xl mx-auto mb-8 bg-[#FAF9F6] border-l-4 border-l-[#1B6C62] px-5 py-4">
 <p className="font-serif text-[15px] text-[#1A1A1A] leading-relaxed">
 A bad agent leaves a trail too. Before you hand over a deposit, see if the property or agent shows up in public tribunal decisions, licence actions or disclosed issues. One search across every Australian register. Five minutes now can save you a year of grief.
 </p>
 </div>

 {/* Form */}
 <form onSubmit={submit} className="bg-white border border-[#E5E4DE] p-6 sm:p-8 shadow-sm" data-testid="property-check-form">
 <div className="space-y-4">
 <div>
 <label className="label-mono block mb-2">Property address</label>
 <div className="relative">
 <MapPin size={16} strokeWidth={1.5} className="absolute left-3 top-3.5 text-[#6B7280]" />
 <input
 type="text"
 value={address}
 onChange={(e) => setAddress(e.target.value)}
 placeholder="e.g. 42 King Street, Brisbane QLD 4000"
 className="input-field pl-10"
 autoFocus
 data-testid="check-address-input"
 />
 </div>
 </div>
 <div className="grid sm:grid-cols-2 gap-4">
 <div>
 <label className="label-mono block mb-2">State</label>
 <select value={state} onChange={(e) => setState(e.target.value)} className="input-field" data-testid="check-state-select">
 {STATES.map(s => <option key={s.code} value={s.code}>{s.code}, {s.name}</option>)}
 </select>
 </div>
 <div>
 <label className="label-mono block mb-2">Email <span className="text-[#6B7280] normal-case font-serif italic text-[10px]">(so we can save your search)</span></label>
 <input
 type="email"
 value={email}
 onChange={(e) => setEmail(e.target.value)}
 placeholder="you@example.com"
 className="input-field"
 data-testid="check-email-input"
 />
 </div>
 </div>
 {error && (
 <div className="text-sm text-[#B35D4C] flex items-center gap-2" data-testid="check-error">
 <AlertCircle size={14} strokeWidth={1.75} /> {error}
 </div>
 )}
 <button
 type="submit"
 disabled={submitting}
 className="btn-primary w-full inline-flex items-center justify-center gap-2"
 data-testid="check-submit"
 >
 {submitting ? (
 <><Loader2 size={16} className="animate-spin" /> Searching public registers…</>
 ) : (
 <><Search size={16} strokeWidth={1.75} /> Run the check</>
 )}
 </button>
 <p className="text-[11px] text-[#6B7280] font-serif italic text-center leading-relaxed">
 We don&apos;t make claims about any property or person. We point you to public Australian registers and let you read the primary sources yourself.
 </p>
 </div>
 </form>

 {/* Results */}
 {result && (
 <div id="check-results" className="mt-10 space-y-6" data-testid="check-results">
 <div className="bg-white border border-[#E5E4DE] p-6 sm:p-8">
 <div className="label-mono mb-2">Searching</div>
 <h2 className="font-serif text-2xl text-[#0D2B52] mb-1">{result.address}</h2>
 <p className="text-sm text-[#6B7280]">{result.state || "All states"}</p>
 </div>

 {/* AI briefing */}
 {result.briefing && (
 <div className="bg-white border border-[#E5E4DE] p-6 sm:p-8" data-testid="check-briefing">
 <div className="flex items-center gap-2 mb-4">
 <Briefcase size={18} strokeWidth={1.5} className="text-[#1B6C62]" />
 <h3 className="font-serif text-xl text-[#0D2B52]">What to look for</h3>
 </div>
 <div className="space-y-4 text-[15px] text-[#1A1A1A] leading-relaxed font-serif">
 {result.briefing.split(/\n\s*\n/).map((para, i) => (
 <p key={i}>
 <RichText text={para.trim()} />
 </p>
 ))}
 </div>
 </div>
 )}

 {/* Public-register links, grouped */}
 <div className="space-y-4">
 {Object.entries(grouped).map(([cat, items]) => {
 const Icon = CATEGORY_ICON[cat] || ExternalLink;
 return (
 <div key={cat} className="bg-white border border-[#E5E4DE]" data-testid={`check-category-${cat}`}>
 <div className="px-6 py-3 border-b border-[#E5E4DE] flex items-center gap-2 bg-[#FAF7F0]">
 <Icon size={16} strokeWidth={1.5} className="text-[#1B6C62]" />
 <span className="label-mono">{CATEGORY_LABEL[cat] || cat}</span>
 </div>
 <div className="divide-y divide-[#E5E4DE]">
 {items.map((s, i) => (
 <a key={i} href={s.url} target="_blank" rel="noopener noreferrer"
 className="block px-6 py-4 hover:bg-[#FAF7F0] transition-colors group"
 data-testid={`check-source-${s.source}`}>
 <div className="flex items-start justify-between gap-3">
 <div className="min-w-0 flex-1">
 <div className="font-serif text-base text-[#0D2B52] group-hover:underline">{s.label}</div>
 <p className="text-sm text-[#6B7280] leading-relaxed mt-1">{s.description}</p>
 </div>
 <ExternalLink size={14} strokeWidth={1.5} className="text-[#6B7280] mt-1 shrink-0 group-hover:text-[#1B6C62]" />
 </div>
 </a>
 ))}
 </div>
 </div>
 );
 })}
 </div>

 <div className="border border-[#C9A961]/40 bg-[#FAF6E8] p-5" data-testid="check-disclaimer">
 <p className="text-sm text-[#8A6D3B] font-serif italic leading-relaxed">{result.disclaimer}</p>
 </div>

 {/* CTA. start a case before signing */}
 <div className="bg-[#0D2B52] text-white p-6 sm:p-8 text-center" data-testid="check-start-case-cta">
 <h3 className="font-serif text-2xl mb-3">Already inspected the place?</h3>
 <p className="text-white/80 mb-5 max-w-xl mx-auto leading-relaxed">
 Start a TenantTrail case <em>before</em> you sign. Upload your open-home photos with EXIF intact, save the lease draft and your audit trail begins at handover, not after the first dispute.
 </p>
 <Link to="/register" className="inline-block bg-[#1B6C62] hover:brightness-95 text-white px-7 py-2.5 transition-all font-medium" data-testid="check-cta-signup">
 Start a free case
 </Link>
 </div>
 </div>
 )}
 
      <SiteFooter />
      </main>
 </div>
 );
}
